/* Hallmark · component: Card · genre: modern-minimal · theme: maeiz-tokens
 * motion: none — composed surfaces for content blocks
 */
import React from "react";
import { clsx } from "clsx";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
    variant?: "default" | "elevated" | "outlined" | "ghost";
    padding?: "none" | "sm" | "md" | "lg";
}

export const Card: React.FC<CardProps> = ({
    children,
    variant = "default",
    padding = "md",
    className,
    ...props
}) => {
    const variantStyles = {
        default: "bg-white shadow-mz-sm border-neutral-border/80 dark:bg-neutral-navy dark:border-white/[0.08]",
        elevated: "bg-white shadow-mz-md border-neutral-border/60 dark:bg-neutral-navy dark:shadow-mz-lg dark:border-white/[0.1]",
        outlined: "bg-white border-neutral-border shadow-none dark:bg-transparent dark:border-white/[0.14]",
        ghost: "bg-transparent border-transparent shadow-none",
    };

    const paddingStyles = {
        none: "",
        sm: "p-3",
        md: "p-4 sm:p-5",
        lg: "p-5 sm:p-6",
    };

    return (
        <div
            className={clsx(
                "rounded-2xl border text-neutral-ink dark:text-slate-100",
                variantStyles[variant],
                paddingStyles[padding],
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
};

export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div className={clsx("mb-3 flex flex-col gap-1", className)} {...props} />
);

export const CardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ className, ...props }) => (
    <h3 className={clsx("font-display text-base font-semibold text-neutral-dark dark:text-white", className)} {...props} />
);

export const CardDescription: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({ className, ...props }) => (
    <p className={clsx("text-sm text-neutral-secondary", className)} {...props} />
);

export const CardContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div className={clsx("text-sm text-neutral-ink dark:text-slate-200", className)} {...props} />
);

export const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div
        className={clsx(
            "mt-4 flex flex-wrap items-center gap-2 border-t border-neutral-border/70 pt-3 dark:border-white/10",
            className,
        )}
        {...props}
    />
);

export const CardMedia: React.FC<React.HTMLAttributes<HTMLDivElement> & { src?: string; alt?: string }> = ({
    className,
    src,
    alt = "",
    children,
    ...props
}) => (
    <div className={clsx("-mx-[inherit] mb-4 overflow-hidden rounded-xl bg-neutral-light dark:bg-white/5", className)} {...props}>
        {src ? <img src={src} alt={alt} className="h-36 w-full object-cover" /> : children}
    </div>
);
